/**
 * Client-side helper for the W3 photo-to-plan workflow.
 *
 * Pipeline:
 *   1. User picks one (or two) room photos.
 *   2. We base64-encode them and POST /api/recon/scan.
 *   3. The Pages Function forwards through APISIX with
 *      `X-LLM-Model: comfy-vggt-room` (CLAUDE.md §3 — one gateway).
 *   4. The recon service returns a typed ReconstructionResult.
 *   5. `ingestReconstruction()` writes the room + IN_ROOM SKUs into
 *      the active project's user-placement layer so the result is
 *      immediately editable through the same drag-drop path catalog
 *      adds use today.
 *
 * Confidence floor is enforced in the UI layer (VITE_INTAKE_CONFIDENCE_FLOOR)
 * — this lib stays pure-transform.
 */
import { addPlacement, setPlacementPosition } from "./userPlacements";

export interface ReconstructionResult {
  readonly room: {
    readonly polygonM: ReadonlyArray<{ x: number; z: number }>;
    readonly heightM: number;
    readonly roomKind: string;
    readonly confidence: number;
  };
  readonly skus: ReadonlyArray<{
    readonly category: string;
    readonly bboxPx: { x: number; y: number; w: number; h: number };
    readonly posM: { x: number; z: number };
    readonly yawDeg: number;
    readonly confidence: number;
  }>;
  readonly camera: { fovDeg: number; pitchDeg: number; heightM: number };
  readonly renderedPreviewUrl?: string;
}

export type ReconResponse =
  | { status: "ok"; result: ReconstructionResult }
  | { status: "pending"; prompt_id: string; client_id: string; poll_at: string }
  | { error: string; hint?: string; detail?: string };

export async function fileToBase64(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const r = reader.result;
      if (typeof r !== "string") reject(new Error("read failed"));
      else resolve(r);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

export async function reconstructPhoto(args: {
  primary: File;
  secondary?: File;
  roomKindHint?: string;
  doorHeightM?: number;
  projectId?: string;
}): Promise<ReconResponse> {
  const primary = await fileToBase64(args.primary);
  const secondary = args.secondary ? await fileToBase64(args.secondary) : undefined;
  const body: Record<string, unknown> = { primaryImageBase64: primary };
  if (secondary) body.secondaryImageBase64 = secondary;
  if (args.roomKindHint) body.roomKindHint = args.roomKindHint;
  if (typeof args.doorHeightM === "number") body.doorHeightM = args.doorHeightM;
  if (args.projectId) body.projectId = args.projectId;

  const r = await fetch("/api/recon/scan", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  try {
    return JSON.parse(text) as ReconResponse;
  } catch {
    return { error: "non_json_response", hint: text.slice(0, 200) };
  }
}

/**
 * Ingest a reconstruction into the active project graph as user-layer
 * placements. We don't synthesize new SKU ontology nodes from the
 * reconstruction — instead we land them as typed catalog SKUs that
 * merge through the same path drag-drop uses (mergeUserPlacements).
 *
 * For V1 the room itself is *not* created from polygonM (the existing
 * Room ontology nodes carry size_id presets); the SKUs land in the
 * caller-supplied roomId. A later round adds a `:Room` creation path
 * keyed on polygonM when there's no matching room.
 */
export function ingestReconstruction(args: {
  projectId: string;
  roomId: string;
  result: ReconstructionResult;
  catalogSkuByCategory: ReadonlyMap<string, string>;   // category → SKU id
  confidenceFloor: number;
}): { added: number; skipped: number; lowConfidence: number } {
  let added = 0, skipped = 0, lowConfidence = 0;
  for (const sku of args.result.skus) {
    if (sku.confidence < args.confidenceFloor) { lowConfidence += 1; continue; }
    const skuId = args.catalogSkuByCategory.get(sku.category);
    if (!skuId) { skipped += 1; continue; }
    addPlacement(args.projectId, skuId, args.roomId);
    setPlacementPosition(args.projectId, skuId, args.roomId, sku.posM.x, sku.posM.z, sku.yawDeg);
    added += 1;
  }
  return { added, skipped, lowConfidence };
}
